// Личные ассистенты (P0): продуктовые типы UI. Источник правды — серверные
// metadata.AiAssistantTemplate/AiAssistantBinding (zdc.proto), здесь — только
// форма для экранов. Поля не дублируют назначения агента/модели (assignments).

// Канал доставки: базовые значения для подсказок, но ядро channel-agnostic —
// допускаем любое строковое значение, чтобы не терять каналы поставки.
export type AssistantChannel =
  | 'chat'
  | 'telegram'
  | 'max'
  | 'email'
  | 'webpush'
  | 'in_app'
  | (string & {});

export const ASSISTANT_CHANNEL_OPTIONS: ReadonlyArray<{ code: AssistantChannel; label: string }> = [
  { code: 'chat', label: 'Внутренний чат' },
  { code: 'telegram', label: 'Telegram' },
  { code: 'max', label: 'MAX' },
  { code: 'email', label: 'Электронная почта' },
  { code: 'webpush', label: 'Web Push' },
  { code: 'in_app', label: 'В приложении' },
];

export type AssistantMemoryScope = '' | 'global' | 'session' | 'per_user';

export const ASSISTANT_MEMORY_SCOPE_OPTIONS: ReadonlyArray<{ code: AssistantMemoryScope; label: string }> = [
  { code: '', label: 'Как у агента' },
  { code: 'global', label: 'Общая память базы' },
  { code: 'session', label: 'История диалога' },
  { code: 'per_user', label: 'Личная память пользователя' },
];

export type AssistantSource = 'binding' | 'assignment' | 'system' | 'requested';

// Квоты: 0 = без ограничения. Стоимость — в минорных единицах (центы).
export interface AssistantQuota {
  maxTokensPerDay: number;
  maxRequestsPerHour: number;
  maxCostCentsPerDay: number;
  maxContextTokens: number;
}

export function emptyAssistantQuota(): AssistantQuota {
  return { maxTokensPerDay: 0, maxRequestsPerHour: 0, maxCostCentsPerDay: 0, maxContextTokens: 0 };
}

export function hasAssistantQuota(q: AssistantQuota | null): boolean {
  if (!q) return false;
  return q.maxTokensPerDay > 0 || q.maxRequestsPerHour > 0
    || q.maxCostCentsPerDay > 0 || q.maxContextTokens > 0;
}

export interface AssistantPolicy {
  userCanOverrideModel: boolean;
  userCanEditStyle: boolean;
  userCanEditMemory: boolean;
  approvalTtlMinutes: number;
}

export interface AssistantStylePreset {
  code: string;
  name: string;
  tone: string;
}

export interface AssistantTemplateInput {
  code: string;
  name: string;
  description: string;
  agentCode: string;
  providerId: string;
  defaultModelId: string;
  // Сохраняются как есть (редактор UI их не меняет) — без потери данных.
  modelMapping: Record<string, string>;
  stylePresets: AssistantStylePreset[];
  basePrompt: string;
  guardPrompt: string;
  tools: string[];
  toolsRestricted: boolean;
  destructiveTools: string[];
  memoryScope: AssistantMemoryScope;
  memoryTtlDays: number;
  allowUserWipe: boolean;
  quotas: AssistantQuota;
  policy: AssistantPolicy;
  channels: AssistantChannel[];
  isEnabled: boolean;
  isPublic: boolean;
}

export interface AssistantTemplate extends AssistantTemplateInput {
  id: string;
  isSystem: boolean;
  templateId: string;
  version: number;
  updatedAt: string;
  // Политика базы: шаблон автоматически назначается новым пользователям.
  // Меняется ТОЛЬКО через AssistantDefaults (не формой шаблона).
  isDefaultForNewUsers: boolean;
}

// Настройки ассистента по умолчанию (админ базы).
export interface AssistantDefaults {
  defaultTemplateCode: string; // '' = авто-привязка выключена
  allowSystemDefault: boolean; // системный агент без шаблона доступен пользователям
}

// Доступность закреплённого чата ассистента (ошибки конфигурации — состояние).
export interface MyAssistantAvailability {
  available: boolean;
  reason: string;
  source: string;
  templateCode: string;
  hasBinding: boolean;
  agentCode: string;
  modelId: string;
  providerId: string;
}

// Неизменяемая версия промта шаблона. История только пополняется:
// публикация/откат создают новую запись, старые версии не редактируются.
export interface AssistantPromptVersion {
  id: string;
  templateCode: string;
  version: number;
  basePrompt: string;
  guardPrompt: string;
  rolloutPct: number;
  note: string;
  publishedBy: string;
  publishedAt: string;
  createdAt: string;
}

export interface AssistantPromptVersionInput {
  templateCode: string;
  basePrompt: string;
  guardPrompt: string;
  note: string;
  rolloutPct: number;
}

export interface AssistantBindingOverrides {
  modelId: string;
  style: string;
  memoryScope: AssistantMemoryScope;
  channels: AssistantChannel[];
  language: string;
}

export interface AssistantBindingInput {
  userId: string;
  templateCode: string;
  isDefault: boolean;
  enabled: boolean;
  overrides: AssistantBindingOverrides;
  quotaOverrides: AssistantQuota;
}

export interface AssistantBinding extends AssistantBindingInput {
  id: string;
  assignedBy: string;
  createdAt: string;
  updatedAt: string;
}

export interface AssistantUsage {
  usageDate: string;
  usageHour: number;
  tokensIn: number;
  tokensOut: number;
  costCents: number;
  requests: number;
}

export interface AssistantApproval {
  id: string;
  userId: string;
  templateCode: string;
  bindingId: string;
  toolCode: string;
  args: string;
  status: string;
  requestedAt: string;
  decidedAt: string;
  decidedBy: string;
  expiresAt: string;
}

export interface MyAssistantOverview {
  source: AssistantSource;
  template: AssistantTemplate | null;
  binding: AssistantBinding | null;
  agentCode: string;
  agentName: string;
  modelId: string;
  modelName: string;
  providerId: string;
  quotas: AssistantQuota;
  channels: AssistantChannel[];
  usageToday: AssistantUsage | null;
  pendingApprovals: AssistantApproval[];
}

// Уровни памяти, которые могут принадлежать пользователю. global/current без
// владельца — общая память базы, в «Мой ассистент» не показывается.
export type AssistantMemoryKind = 'fact' | 'episode' | 'note';

// Запись памяти, принадлежащая ТОЛЬКО текущему пользователю (ownerId === мой id).
// importance/updatedAt сервер в AiMemoryNode не отдаёт — не подставляем фиктивные.
export interface AssistantMemoryEntry {
  id: string;
  title: string;
  level: string;
  kind: AssistantMemoryKind;
  content: string;
  ownerId: string;
  conversationId: string;
  createdAt: string;
}

// Политика памяти из шаблона личного ассистента. allowUserWipe = null — шаблон
// не назначен, право очистки определяет сервер.
export interface AssistantMemoryPolicy {
  source: 'template' | 'unknown';
  templateName: string;
  ttlDays: number;
  allowUserWipe: boolean | null;
  memoryScope: AssistantMemoryScope;
}

export interface AssistantToolOption {
  code: string;
  name: string;
  description: string;
}

// ----------------------------- Назначения ассистента (P1) -----------------------------
// Назначение (AssistantAssignment): подмена агента/модели/провайдера для
// пользователя/роли/всех. P1 «Гибкие назначения»: провайдер, ССЫЛКА на ключ
// (base | user:<entity_id> | instance — сам ключ наружу не ходит), суточный
// потолок расхода, включённость, срок действия.

// Режим ключа назначения: Ключ базы | Личный ключ | Инстанс (ИИ-шлюз).
export type AssistantAssignmentKeyMode = 'base' | 'user' | 'instance';

export const ASSISTANT_ASSIGNMENT_KEY_OPTIONS: ReadonlyArray<{ code: AssistantAssignmentKeyMode; label: string; hint: string }> = [
  { code: 'base', label: 'Ключ базы', hint: 'Провайдер и ключ базы (поведение по умолчанию).' },
  { code: 'user', label: 'Личный ключ', hint: 'Личный провайдер пользователя назначения — свой ключ и своя оплата.' },
  { code: 'instance', label: 'Инстанс (ИИ-шлюз)', hint: 'Ключ инстанса; внутренние вызовы ИИ пока не поддерживают этот путь.' },
];

export interface AssistantAssignment {
  id: string;
  scope: string;      // user | role | global
  scopeId: string;    // entity_id | role_id (global — пусто)
  agentCode: string;
  modelId: string;
  style: string;
  providerId: string;
  keyRef: string;     // '' | base | user:<entity_id> | instance
  dailyCostLimit: number; // 0 = без лимита
  isEnabled: boolean;
  validTo: string;    // RFC3339 | '' = бессрочно
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

export interface AssistantAssignmentInput {
  id: string;
  scope: string;
  scopeId: string;
  agentCode: string;
  modelId: string;
  style: string;
  providerId: string;
  keyMode: AssistantAssignmentKeyMode;
  dailyCostLimit: number;
  isEnabled: boolean;
  // Дата срока в виде ГГГГ-ММ-ДД (пусто = бессрочно); при сохранении
  // превращается в RFC3339 (конец дня).
  validFromDate: string;
}

/** Режим ключа из ссылки key_ref сервера. */
export function keyModeFromRef(keyRef: string): AssistantAssignmentKeyMode {
  if (keyRef === 'instance') return 'instance';
  if (keyRef.startsWith('user:')) return 'user';
  return 'base';
}

/** Ссылка на ключ из режима (личный ключ — пользователь назначения). */
export function keyRefFromMode(mode: AssistantAssignmentKeyMode, scopeId: string): string {
  if (mode === 'instance') return 'instance';
  if (mode === 'user') return `user:${scopeId}`;
  return 'base';
}

/** Назначение истекло (valid_to в прошлом). */
export function isAssignmentExpired(validTo: string): boolean {
  if (!validTo) return false;
  const at = new Date(validTo).getTime();
  return Number.isFinite(at) && at <= Date.now();
}
